import { Tags } from 'jsmediatags/types';
import { Sources } from '../models/source';
import Track from '../models/track/track';

/**
 * Track search.
 *
 * The filter box used to be a single `indexOf` against five fields, which for
 * a 40k library is not enough to tell two "Intro" apart. This is still plain
 * substring matching - no fuzz, no scoring, no ranking - but:
 *
 * - the query splits on whitespace and every term must match (AND), each term
 *   against any field (OR). "daft alive" finds Daft Punk's Alive without
 *   caring which field holds which word, and without matching every track
 *   whose title contains "alive".
 * - terms can name a field: `artist:daft alive`. That is the disambiguator
 *   when a title and an artist share a word.
 * - `"quoted phrases"` keep their spaces, and a leading `-` excludes.
 * - matching is diacritic-insensitive, so `bjork` finds Björk and `motorhead`
 *   finds Motörhead.
 *
 * The normalized fields are cached per Track object, because the alternative
 * is lowercasing 40,000 * 6 strings on every keystroke.
 */

/**
 * NFD decomposes accents into combining marks we can strip, but it leaves
 * these alone - they are letters in their own right, not accented Latin.
 * Icelandic and Nordic names are common enough in this library to be worth
 * the extra pass.
 */
const nonDecomposing: { [character: string]: string } = {
  ð: 'd',
  þ: 'th',
  æ: 'ae',
  ø: 'o',
  œ: 'oe',
  ß: 'ss',
  ł: 'l',
  đ: 'd',
  ħ: 'h',
  ı: 'i',
};

/**
 * Lowercase, strip accents, fold the letters NFD will not touch.
 * Both the query and the track fields go through this, so they meet halfway.
 */
export function normalizeForSearch(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ðþæøœßłđħı]/g, (character) => nonDecomposing[character]);
}

/**
 * `all` is every other field joined, and is what an unqualified term matches
 * against. The join uses a newline so a quoted phrase cannot accidentally
 * straddle two fields.
 */
interface SearchFields {
  title: string;
  artist: string;
  album: string;
  genre: string;
  type: string;
  year: string;
  albumArtist: string;
  composer: string;
  comment: string;
  trackNumber: string;
  disc: string;
  bpm: string;
  initialKey: string;
  publisher: string;
  format: string;
  bitrate: string;
  tags: string;
  all: string;
}

export type SearchField = keyof SearchFields;

const fieldAliases: { [alias: string]: SearchField } = {
  title: 'title',
  name: 'title',
  track: 'title',
  artist: 'artist',
  by: 'artist',
  album: 'album',
  genre: 'genre',
  type: 'type',
  source: 'type',
  year: 'year',
  date: 'year',
  albumartist: 'albumArtist',
  aartist: 'albumArtist',
  composer: 'composer',
  writer: 'composer',
  comment: 'comment',
  note: 'comment',
  trackno: 'trackNumber',
  no: 'trackNumber',
  disc: 'disc',
  disk: 'disc',
  bpm: 'bpm',
  key: 'initialKey',
  publisher: 'publisher',
  label: 'publisher',
  format: 'format',
  ext: 'format',
  extension: 'format',
  bitrate: 'bitrate',
  kbps: 'bitrate',
  tag: 'tags',
  tags: 'tags',
};

/**
 * jsmediatags hands back frames as either a plain value or a
 * `{ id, description, data }` object, and `data` is itself sometimes an
 * object (or a picture's byte array, which we never want in a haystack).
 * One level deep is all the text there is.
 */
function tagText(tags: Tags | undefined) {
  if (!tags) return '';

  const parts: string[] = [];

  for (const key in tags) {
    const frame = (tags as any)[key];

    if (typeof frame === 'string' || typeof frame === 'number') {
      parts.push(String(frame));
    } else if (frame && typeof frame.data === 'string') {
      parts.push(frame.data);
    } else if (frame && typeof frame.data === 'number') {
      parts.push(String(frame.data));
    }
  }

  return parts.join('\n');
}

/**
 * Keyed on the Track object rather than on its id, so tracks dropped from the
 * library take their entry with them. Tracks are mutable - a tag refetch
 * rewrites title/artist/album/genre in place - so writers call
 * `invalidateTrackSearch` afterwards.
 */
const fieldCache = new WeakMap<Track, SearchFields>();

export function invalidateTrackSearch(track: Track) {
  fieldCache.delete(track);
}

function searchFieldsFor(track: Track): SearchFields {
  const cached = fieldCache.get(track);

  if (cached) return cached;

  const title = normalizeForSearch(track.title || '');
  const artist = normalizeForSearch(track.artist || '');
  const album = normalizeForSearch(track.album || '');
  const genre = normalizeForSearch(track.genre || '');
  const type = normalizeForSearch(Sources[track.type]?.name || '');
  // releaseDate is a timestamp, 0 when unknown. The year is the only part of
  // it anyone types into a search box.
  const releaseYear = track.releaseDate ? new Date(track.releaseDate).getFullYear() : NaN;
  const year = isNaN(releaseYear) ? '' : String(releaseYear);
  // Always '' today - libraryService never stores the raw frames (see
  // docs/TAG-EDITING.md). Wired up so it searches the moment it does.
  const tags = normalizeForSearch(tagText(track.tags));

  // Tag-derived extras. Undefined until a tag read fills them, and the
  // numeric ones are stored as numbers, so everything goes through String().
  const albumArtist = normalizeForSearch(track.albumArtist || '');
  const composer = normalizeForSearch(track.composer || '');
  const comment = normalizeForSearch(track.comment || '');
  const trackNumber = track.trackNumber ? String(track.trackNumber) : '';
  const disc = track.disc ? String(track.disc) : '';
  const bpm = track.bpm ? String(track.bpm) : '';
  const initialKey = normalizeForSearch(track.initialKey || '');
  const publisher = normalizeForSearch(track.publisher || '');

  // The file fields: already lower case, and a number.
  const format = track.format || '';
  const bitrate = track.bitrate ? String(track.bitrate) : '';

  const fields: SearchFields = {
    title,
    artist,
    album,
    genre,
    type,
    year,
    tags,
    albumArtist,
    composer,
    comment,
    trackNumber,
    disc,
    bpm,
    initialKey,
    publisher,
    format,
    bitrate,
    // Track and disc numbers stay out of the combined haystack. They are bare
    // small integers, so an unqualified "3" would otherwise match a large
    // slice of the library. Reachable as trackno: and disc:. The format and
    // bit rate stay out for the same reason - "mp3" is most of any library
    // - and are reachable as format: and bitrate:.
    all: [
      title,
      artist,
      album,
      genre,
      type,
      year,
      tags,
      albumArtist,
      composer,
      comment,
      initialKey,
      publisher,
    ].join('\n'),
  };

  fieldCache.set(track, fields);

  return fields;
}

export interface QueryClause {
  /** Unset means "match against any field". */
  field?: SearchField;
  /** Already normalized, ready for indexOf. */
  value: string;
  negated: boolean;
}

const whitespace = /\s/;

/**
 * Splits a raw query into clauses. Everything is ANDed.
 *
 * Only *known* field names are treated as a prefix, so "AC:DC" stays a single
 * literal term instead of becoming a search of a nonexistent "ac" field.
 */
export function parseTrackQuery(raw: string | undefined): QueryClause[] {
  if (!raw) return [];

  const clauses: QueryClause[] = [];
  let index = 0;

  while (index < raw.length) {
    if (whitespace.test(raw[index])) {
      index++;
      continue;
    }

    let negated = false;

    if (raw[index] === '-' && index + 1 < raw.length && !whitespace.test(raw[index + 1])) {
      negated = true;
      index++;
    }

    let field: SearchField | undefined;
    const colon = raw.indexOf(':', index);

    if (colon !== -1) {
      const candidate = raw.slice(index, colon).toLowerCase();

      if (fieldAliases[candidate]) {
        field = fieldAliases[candidate];
        index = colon + 1;
      }
    }

    let value: string;

    if (raw[index] === '"') {
      const closing = raw.indexOf('"', index + 1);
      // An unterminated quote takes the rest of the string, so the results
      // keep narrowing while the user is still typing the phrase.
      value = closing === -1 ? raw.slice(index + 1) : raw.slice(index + 1, closing);
      index = closing === -1 ? raw.length : closing + 1;
    } else {
      let end = index;
      while (end < raw.length && !whitespace.test(raw[end])) end++;
      value = raw.slice(index, end);
      index = end;
    }

    if (value) {
      clauses.push({
        field,
        value: normalizeForSearch(value),
        negated,
      });
    }
  }

  return clauses;
}

export function matchesTrackQuery(track: Track, clauses: QueryClause[]) {
  if (!clauses.length) return true;

  const fields = searchFieldsFor(track);

  for (const clause of clauses) {
    const haystack = clause.field ? fields[clause.field] : fields.all;
    const hit = haystack.indexOf(clause.value) !== -1;

    // A negated clause has to miss, a normal one has to hit.
    if (hit === clause.negated) return false;
  }

  return true;
}
